// packages
import { useMemo } from 'react'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceArea, ReferenceLine, CartesianGrid } from 'recharts'
import { format, parse, isValid } from 'date-fns'

// entities
import { ExamCollection, marker_status } from '@/entities/exam-analysis'
import { MarkerDef, MarkerRange } from './markers/types'
import { classifyMarker, parseNumber, STATUS_LABEL } from './marker-utils'

interface MarkerInsightPanelProps {
  marker: MarkerDef
  range?: MarkerRange
  collections: ExamCollection[]
  valuesByCollection: Record<string, string>
}

const COLOR_OK = '#10b981' // emerald-500
const COLOR_BAD = '#ef4444' // red-500
const COLOR_WARN = '#f59e0b' // amber-500
const COLOR_EMPTY = '#cbd5e1' // slate-300

function statusColor(s: marker_status): string {
  if (s === marker_status.OK) return COLOR_OK
  if (s === marker_status.ATTENTION) return COLOR_WARN
  if (s === marker_status.ABOVE || s === marker_status.BELOW) return COLOR_BAD
  return COLOR_EMPTY
}

function fmtDateLabel(iso?: string | null, idx?: number): string {
  if (iso) {
    const d = parse(iso, 'yyyy-MM-dd', new Date())
    if (isValid(d)) return format(d, 'dd/MM/yy')
  }
  return idx !== undefined ? `Coleta ${idx + 1}` : '—'
}

export function MarkerInsightPanel({ marker, range, collections, valuesByCollection }: MarkerInsightPanelProps) {
  const data = useMemo(() => {
    return collections.map((c, idx) => {
      const raw = valuesByCollection[c.ref] ?? ''
      const n = raw.trim() === '' ? null : parseNumber(raw)
      const status = classifyMarker(raw, range)
      return {
        idx,
        label: fmtDateLabel(c.date, idx),
        rawLabel: raw,
        value: n !== null && Number.isFinite(n) ? n : null,
        status,
        color: statusColor(status)
      }
    })
  }, [collections, valuesByCollection, range])

  const hasAnyValue = data.some(d => d.value !== null)
  const min = range?.min
  const max = range?.max

  // Domínio Y: inclui valores + faixa ideal com pequena folga
  const yDomain = useMemo((): [number, number] | undefined => {
    const nums = data.filter(d => d.value !== null).map(d => d.value as number)
    if (min !== undefined) nums.push(min)
    if (max !== undefined) nums.push(max)
    if (nums.length === 0) return undefined
    const minV = Math.min(...nums)
    const maxV = Math.max(...nums)
    const pad = Math.max((maxV - minV) * 0.15, Math.abs(maxV) * 0.05, 0.5)
    return [minV - pad, maxV + pad]
  }, [data, min, max])

  return (
    <div className="rounded-lg border border-coentro/15 bg-primary/5 px-4 py-3 my-2">
      <div className="grid grid-cols-1 lgAndUp:grid-cols-[1fr_2fr] gap-4 items-start">
        {/* Texto contextual */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-coentro mb-1">Sobre este marcador</div>
          <div className="text-sm text-foreground/85 leading-relaxed mb-2">
            {marker.obs ? marker.obs : 'Acompanhe a evolução do marcador ao longo das coletas. Valores fora da faixa ideal merecem investigação clínica orientada pela metodologia Mãe Nutrida.'}
          </div>
          <div className="text-[11px] text-muted-foreground">
            <span className="font-semibold text-foreground/80">Referência ideal:</span> {range?.display ?? '—'}
          </div>
          {/* Resumo das coletas */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {data.map(d => (
              <span
                key={d.idx}
                className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ring-1"
                style={{ color: d.color, borderColor: d.color, backgroundColor: `${d.color}1a` }}
              >
                <span className="font-bold">{d.label}</span>
                <span>·</span>
                <span>{d.value !== null ? d.rawLabel : '—'}</span>
                {d.status !== marker_status.EMPTY && (
                  <span className="opacity-80">({STATUS_LABEL[d.status]})</span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Gráfico de evolução */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-coentro mb-1">Evolução nas coletas</div>
          {hasAnyValue ? (
            <div className="h-[160px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={{ stroke: 'hsl(var(--border))' }} tickLine={false} />
                  <YAxis domain={yDomain} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={{ stroke: 'hsl(var(--border))' }} tickLine={false} width={40} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))', fontSize: 12 }}
                    formatter={(value: any) => [`${value}${range?.unit ? ` ${range.unit}` : ''}`, marker.name]}
                  />
                  {min !== undefined && max !== undefined && (
                    <ReferenceArea y1={min} y2={max} fill={COLOR_OK} fillOpacity={0.08} stroke={COLOR_OK} strokeOpacity={0.3} strokeDasharray="3 3" />
                  )}
                  {min !== undefined && max === undefined && (
                    <ReferenceLine y={min} stroke={COLOR_OK} strokeDasharray="3 3" strokeOpacity={0.6} label={{ value: `≥ ${min}`, fontSize: 10, fill: COLOR_OK, position: 'insideTopLeft' }} />
                  )}
                  {max !== undefined && min === undefined && (
                    <ReferenceLine y={max} stroke={COLOR_OK} strokeDasharray="3 3" strokeOpacity={0.6} label={{ value: `≤ ${max}`, fontSize: 10, fill: COLOR_OK, position: 'insideTopLeft' }} />
                  )}
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="hsl(var(--coentro))"
                    strokeWidth={2}
                    connectNulls
                    isAnimationActive={false}
                    dot={({ cx, cy, payload }) => {
                      if (payload?.value === null || payload?.value === undefined) {
                        return <g key={`empty-${payload?.idx}`} />
                      }
                      return (
                        <circle
                          key={`dot-${payload.idx}`}
                          cx={cx}
                          cy={cy}
                          r={5}
                          fill={payload.color}
                          stroke="white"
                          strokeWidth={2}
                        />
                      )
                    }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[120px] flex items-center justify-center text-xs text-muted-foreground italic border border-dashed border-border rounded-md">
              Preencha valores nas coletas para visualizar a evolução.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
